/**
 * Runtime environment metadata for the CampusHub web bundle.
 *
 * This module is the SINGLE source of truth for "which env am I in?"
 * across the React app. Anything that wants to gate UI on env (debug
 * pills, Sentry tags, About-screen content, feature flags) should
 * import from here instead of reading `import.meta.env` directly —
 * that way the gating rules are testable + greppable.
 *
 * Values come from build-time env vars set by `scripts/build-native.sh`
 * (which sources `.env.{env}.native`):
 *
 *   • VITE_ENV_NAME    — "dev" | "staging" | "production"
 *   • VITE_APP_VERSION — semver from apps/web/package.json (every env)
 *   • VITE_APP_BUILD   — CI build number (native only; Play versionCode)
 *   • VITE_GIT_SHA     — short git SHA at build time
 *   • VITE_CAP_DEV     — "1" only in dev (drives the build-stamp pill etc.)
 *   • VITE_SENTRY_DSN  — env-specific DSN, empty disables Sentry
 *
 * For pure web builds (npm run build) these are all unset and we
 * default to a benign "web" identity.
 */

import { version as PACKAGE_VERSION } from "../../package.json";

export type EnvName = "dev" | "staging" | "production" | "web";

const RAW_ENV = ((import.meta.env["VITE_ENV_NAME"] as string | undefined) ?? "").toLowerCase();

/**
 * The environment this bundle was built for. Falls back to "web" in
 * non-native builds where VITE_ENV_NAME isn't set.
 */
export const APP_ENV: EnvName =
  RAW_ENV === "dev" || RAW_ENV === "staging" || RAW_ENV === "production"
    ? (RAW_ENV as EnvName)
    : "web";

/**
 * The marketing version's single source of truth: apps/web/package.json
 * "version" — the same field CI reads for VITE_APP_VERSION and the native
 * projects' versionName. Imported by name so the bundle carries only the
 * string, not the whole manifest.
 */
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.]+)?$/;

/**
 * A user-facing version, or the package version when the env value is not
 * one. `.env.production.example` once documented VITE_APP_VERSION as
 * "git-sha-from-ci", and native builds used to stamp "<branch>-<sha>" — so
 * anything that is not plain semver (a branch, a SHA, an env name) is
 * rejected here rather than printed under Sign out.
 */
export function userFacingVersion(raw: string | undefined, fallback: string = PACKAGE_VERSION): string {
  const v = (raw ?? "").trim();
  return SEMVER.test(v) ? v : fallback;
}

/** A build number is digits or nothing — never a SHA or a branch. */
export function userFacingBuild(raw: string | undefined): string {
  const b = (raw ?? "").trim();
  return /^\d{1,9}$/.test(b) ? b : "";
}

/** Build-time semver (apps/web/package.json "version"). Always non-empty. */
export const APP_VERSION = userFacingVersion(import.meta.env["VITE_APP_VERSION"] as string | undefined);

/**
 * CI build number for native builds — the Play versionCode / App Store build
 * (native-release.yml stamps max(.native-build-number, run_number)). Empty
 * for web builds, which have no build number.
 */
export const APP_BUILD = userFacingBuild(import.meta.env["VITE_APP_BUILD"] as string | undefined);

/** Short git SHA at build time. Useful for bug-report templates. */
export const GIT_SHA = ((import.meta.env["VITE_GIT_SHA"] as string | undefined) || "nogit").trim();

/**
 * "2.0.73 (1385)" — the version the stores show, and the one to print under
 * Sign out and in footers. Web has no build number, so it is just "2.0.73".
 * A commit or branch never belongs here; that is `buildIdentityString()`.
 */
export function versionLabel(version: string = APP_VERSION, build: string = APP_BUILD): string {
  const v = userFacingVersion(version);
  const b = userFacingBuild(build);
  return b ? `${v} (${b})` : v;
}

export const APP_VERSION_LABEL = versionLabel();

/** True when this build allows cleartext + shows debug affordances. */
export const IS_DEV_BUILD = APP_ENV === "dev" || String(import.meta.env["VITE_CAP_DEV"] ?? "") === "1";

/** Sentry DSN for THIS env, or empty string when Sentry is disabled. */
export const SENTRY_DSN = ((import.meta.env["VITE_SENTRY_DSN"] as string | undefined) || "").trim();

